import AirDatepicker, { AirDatepickerOptions } from 'air-datepicker'
import 'air-datepicker/air-datepicker.css'
import moment from 'moment/moment'
import { useEffect, useRef, useState } from 'react'

import { useModalContext } from '../../app/design/Modal'
import { TextField } from './TextField'

type Props = {
    selectedDateString?: string
    onSelectDate?: (date: Date) => void
    startDateString?: string
    minDateString?: string
    maxDateString?: string
    placeholder?: string
    size?: 'small' | 'medium'
    disabled?: boolean
}

export const DatePicker = ({
    selectedDateString,
    onSelectDate,
    startDateString,
    minDateString,
    maxDateString,
    placeholder,
    size = 'small',
    disabled = false,
}: Props) => {
    const modalContext = useModalContext()

    const [isDatePickerShowing, setIsDatePickerShowing] = useState(false)

    const textfieldRef = useRef<HTMLInputElement>(null)
    const datepickerRef = useRef<AirDatepicker<HTMLInputElement> | null>(null)

    const onSelectDateRef = useRef(onSelectDate)
    onSelectDateRef.current = onSelectDate

    useEffect(() => {
        const textfield = textfieldRef.current
        if (textfield === null) {
            return
        }

        const datepicker = new AirDatepicker(textfield, {
            buttons: ['today', 'clear'],
            minDate: minDateString,
            maxDate: maxDateString,
            startDate: startDateString,
            classes: 'pointer-events-auto',
            onSelect: ({ datepicker, date }) => {
                const selectedDate = Array.isArray(date) ? date[0] : date

                if (moment(selectedDate).format('MM/DD/YYYY') !== selectedDateString) {
                    onSelectDateRef.current?.(selectedDate)
                }

                try {
                    datepicker.hide()
                }
                catch (_error) {
                    // Ignore errors from hide, it seems to error on something
                    // internally, but this doesn't affect the behavior.
                }
            },
            locale: airDatePickerLocaleOptions,
            onShow: () => setIsDatePickerShowing(true),
            onHide: () => setIsDatePickerShowing(false),
        })
        datepickerRef.current = datepicker

        if (selectedDateString !== undefined) {
            datepicker.selectDate(selectedDateString)
        }

        return () => {
            datepicker?.destroy()
        }
    }, [minDateString, maxDateString, startDateString, selectedDateString])

    useEffect(() => {
        if (datepickerRef.current === null || selectedDateString === undefined) {
            return
        }
        datepickerRef.current.selectDate(selectedDateString)
    }, [selectedDateString])

    useEffect(() => {
        if (modalContext === null || !isDatePickerShowing) {
            return
        }
        modalContext.setShouldDisableCloseOnClickOutside(true)

        return () => {
            modalContext.setShouldDisableCloseOnClickOutside(false)
        }
    }, [isDatePickerShowing, modalContext])

    return (
        <TextField
            ref={textfieldRef}
            readOnly
            icon={size === 'small' ? 'calendar.small' : 'calendar.medium'}
            placeholder={placeholder}
            size={size}
            disabled={disabled}
        />
    )
}

const airDatePickerLocaleOptions: AirDatepickerOptions<HTMLInputElement>['locale'] = {
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    daysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ],
    monthsShort: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ],
    today: 'Today',
    clear: 'Clear',
    dateFormat: 'MMMM d, yyyy',
    timeFormat: 'hh:mm aa',
    firstDay: 0,
}
